/**
 * Transcript nodes: hierarchical structure for visualization and scanning.
 *
 * Transforms flat event streams into a semantic tree with agent-centric interpretation.
 *
 * TypeScript port of Python's nodes.py, implementing our own span tree building
 * since we don't have access to inspect_ai's event_tree().
 */

import type {
  Event,
  ModelEvent,
  Timeline as ServerTimeline,
  TimelineEvent as ServerTimelineEvent,
  TimelineSpan as ServerTimelineSpan,
} from "@tsmono/inspect-common/types";
import { isRecord } from "@tsmono/util";

import { codexToolMarkdown } from "../../chat/tools/tool";

// =============================================================================
// Span Tree Types (internal)
// =============================================================================

/**
 * Internal representation of a span node built from span_begin/span_end events.
 */
interface SpanNode {
  id: string;
  name: string;
  type?: string;
  parentId?: string | null;
  metadata?: Record<string, unknown>;
  children: TreeItem[];
  /** The original span_begin event, preserved for unrolling. */
  beginEvent: Event;
  /** The original span_end event (if any), preserved for unrolling. */
  endEvent?: Event;
}

type TreeItem = SpanNode | Event;

function isSpanNode(item: TreeItem): item is SpanNode {
  return "children" in item && Array.isArray(item.children);
}

// =============================================================================
// Node Types
// =============================================================================

/**
 * Wraps a single Event with computed timing and token methods.
 */
export class TimelineEvent {
  readonly type = "event" as const;
  readonly event: Event;

  constructor(event: Event) {
    this.event = event;
  }

  startTime(): Date {
    return new Date(this.event.timestamp);
  }

  endTime(): Date {
    const completed =
      "completed" in this.event && typeof this.event.completed === "string"
        ? this.event.completed
        : null;
    return completed ? new Date(completed) : this.startTime();
  }

  totalTokens(): number {
    return getEventTokens(this.event);
  }

  idleTime(): number {
    return 0;
  }

  /**
   * Returns true if this event is the fork point for the given `branchedFrom`.
   *
   * `branchedFrom` is an anchor ID; producers emit an `AnchorEvent` at each
   * fork-able point (`timeline_branch()` does this automatically).
   */
  matchesForkPoint(id: string): boolean {
    return this.event.event === "anchor" && this.event.anchor_id === id;
  }
}

/**
 * A span of execution — agent, scorer, tool, or root.
 */
/**
 * Narrow one node of a timeline's `content`, which mixes spans and events.
 * Tests reach for `content[0] as TimelineSpan`; these check the discriminant
 * and report what was actually there.
 */
export const asTimelineSpan = (node: unknown): TimelineSpan => {
  if (!(node instanceof TimelineSpan)) {
    throw new Error(`expected a TimelineSpan, got ${describeNode(node)}`);
  }
  return node;
};

export const asTimelineEvent = (node: unknown): TimelineEvent => {
  if (!(node instanceof TimelineEvent)) {
    throw new Error(`expected a TimelineEvent, got ${describeNode(node)}`);
  }
  return node;
};

const describeNode = (node: unknown): string =>
  isRecord(node) && typeof node["type"] === "string"
    ? `a "${node["type"]}" node`
    : String(node);

export class TimelineSpan {
  readonly type = "span" as const;
  id: string;
  name: string;
  spanType: string | null;
  content: (TimelineEvent | TimelineSpan)[];
  branches: TimelineSpan[];
  branchedFrom: string | null;
  description?: string;
  utility: boolean;
  // True if this agent span was invoked as a tool (via the agent tool /
  // task / as_tool / handoff). Tool-invoked subagents are explicit
  // user-intended sub-trajectories and are never classified as `utility`,
  // even when single-turn (e.g. a cancelled background subagent). Mirrors
  // the Python `TimelineSpan.tool_invoked` flag.
  toolInvoked: boolean;
  agentResult?: string;
  outline?: Outline;

  constructor(props: {
    id: string;
    name: string;
    spanType: string | null;
    content?: (TimelineEvent | TimelineSpan)[];
    branches?: TimelineSpan[];
    branchedFrom?: string | null;
    description?: string;
    utility?: boolean;
    toolInvoked?: boolean;
    agentResult?: string;
    outline?: Outline;
  }) {
    this.id = props.id;
    this.name = props.name;
    this.spanType = props.spanType;
    this.content = props.content ?? [];
    this.branches = props.branches ?? [];
    this.branchedFrom = props.branchedFrom ?? null;
    this.description = props.description;
    this.utility = props.utility ?? false;
    this.toolInvoked = props.toolInvoked ?? false;
    this.agentResult = props.agentResult;
    this.outline = props.outline;
  }

  startTime(includeBranches = true): Date {
    const items: (TimelineEvent | TimelineSpan)[] = includeBranches
      ? [...this.content, ...this.branches]
      : this.content;
    return items.length > 0 ? minStartTime(items) : new Date(0);
  }

  endTime(includeBranches = true): Date {
    const items: (TimelineEvent | TimelineSpan)[] = includeBranches
      ? [...this.content, ...this.branches]
      : this.content;
    return items.length > 0 ? maxEndTime(items) : new Date(0);
  }

  totalTokens(includeBranches = true): number {
    const items: (TimelineEvent | TimelineSpan)[] = includeBranches
      ? [...this.content, ...this.branches]
      : this.content;
    return sumTokens(items);
  }

  idleTime(includeBranches = true): number {
    const items: (TimelineEvent | TimelineSpan)[] = includeBranches
      ? [...this.content, ...this.branches]
      : this.content;
    return computeIdleTime(
      items,
      this.startTime(includeBranches),
      this.endTime(includeBranches)
    );
  }
}

/**
 * True if `span` has no visible content and no non-empty nested branches.
 * `anchor`, `branch`, and `step` events are structural and don't count.
 */
export function isEmptyBranch(span: TimelineSpan): boolean {
  const hasVisibleContent = span.content.some((item) => {
    if (item.type === "span") return true;
    const evt = item.event.event;
    return evt !== "anchor" && evt !== "branch" && evt !== "step";
  });
  return !hasVisibleContent && span.branches.length === 0;
}

/**
 * Return a copy of `timeline` with empty branches pruned recursively.
 * Walks the span tree, pruning empty branches bottom-up so that branches
 * which only carry other (empty) branches are themselves dropped.
 */
export function filterEmptyBranches(timeline: Timeline): Timeline {
  return {
    name: timeline.name,
    description: timeline.description,
    root: pruneEmptyBranches(timeline.root),
  };
}

function pruneEmptyBranches(span: TimelineSpan): TimelineSpan {
  const content = span.content.map((item) =>
    item.type === "span" ? pruneEmptyBranches(item) : item
  );
  const branches = span.branches
    .map(pruneEmptyBranches)
    .filter((b) => !isEmptyBranch(b));
  return new TimelineSpan({
    id: span.id,
    name: span.name,
    spanType: span.spanType,
    content,
    branches,
    branchedFrom: span.branchedFrom,
    description: span.description,
    utility: span.utility,
    toolInvoked: span.toolInvoked,
    agentResult: span.agentResult,
    outline: span.outline,
  });
}

/**
 * True if `span` (or any descendant span in its content tree) has branches.
 */
export function spanHasBranches(span: TimelineSpan): boolean {
  if (span.branches.length > 0) return true;
  for (const item of span.content) {
    if (item.type === "span" && spanHasBranches(item)) return true;
  }
  return false;
}

/**
 * Count utility spans in `span`'s content tree.
 *
 * Used to surface how many utility agents are elided from display when the
 * "Utility agents" option is off, so they never disappear without a trace.
 * Branches are deliberately excluded: the indicator's contract is "what the
 * utility toggle reveals", and branch content stays hidden behind the
 * separate branches option regardless of the utility setting.
 */
export function countUtilitySpans(span: TimelineSpan): number {
  let count = 0;
  for (const item of span.content) {
    if (item.type === "span") {
      if (item.utility) count++;
      count += countUtilitySpans(item);
    }
  }
  return count;
}

/**
 * Creates a display-ready TimelineSpan from a branch span.
 *
 * If the branch has exactly one child span, returns that span directly.
 * Otherwise creates a synthetic container. The returned span's name is
 * clean (no arrow prefix) — callers that need a display prefix (e.g. the
 * swimlane row label) should add it themselves.
 */
export function createBranchSpan(
  branch: TimelineSpan,
  label: string
): TimelineSpan {
  const name = deriveBranchLabel(branch, label);
  return new TimelineSpan({
    id: branch.id,
    name,
    spanType: "branch",
    content: branch.content,
    branches: branch.branches,
    branchedFrom: branch.branchedFrom,
    description: branch.description,
    utility: branch.utility,
    toolInvoked: branch.toolInvoked,
    agentResult: branch.agentResult,
    outline: branch.outline,
  });
}

const kGenericBranchNames = new Set(["", "branch", "trajectory"]);

function deriveBranchLabel(branch: TimelineSpan, label: string): string {
  if (!kGenericBranchNames.has(branch.name)) return branch.name;
  for (const item of branch.content) {
    if (item.type === "span") return item.name;
  }
  return `Branch ${label}`;
}

/**
 * A node in an agent's outline, referencing an event by UUID.
 */
export interface OutlineNode {
  event: string;
  children?: OutlineNode[];
}

/**
 * Hierarchical outline of events for an agent.
 */
export interface Outline {
  nodes: OutlineNode[];
}

/**
 * A named timeline view over a transcript.
 *
 * Multiple timelines allow different interpretations of the same event
 * stream — e.g. a default agent-centric view alongside an alternative
 * grouping or filtered view.
 */
export interface Timeline {
  name: string;
  description: string;
  root: TimelineSpan;
}

// =============================================================================
// Server Timeline Conversion
// =============================================================================

/**
 * Build an event lookup map from a flat event array, keyed by UUID.
 */
function buildEventLookup(events: Event[]): Map<string, Event> {
  const map = new Map<string, Event>();
  for (const event of events) {
    const uuid = (event as { uuid?: string | null }).uuid;
    if (uuid) {
      map.set(uuid, event);
    }
  }
  return map;
}

/**
 * Convert a server-provided Timeline (snake_case) to the client-side Timeline
 * (camelCase with computed Date/token/idle properties).
 *
 * Server timelines store event references as UUID strings. The `events` array
 * is used to resolve these references to full Event objects.
 */
export function convertServerTimeline(
  server: ServerTimeline,
  events: Event[]
): Timeline {
  const lookup = buildEventLookup(events);
  const root = convertServerSpan(server.root, lookup);
  return { name: server.name, description: server.description, root };
}

function convertServerContentItem(
  item: ServerTimelineEvent | ServerTimelineSpan,
  lookup: Map<string, Event>
): TimelineEvent | TimelineSpan | null {
  if (item.type === "event") {
    return convertServerEvent(item, lookup);
  }
  return convertServerSpan(item, lookup);
}

function convertServerEvent(
  server: ServerTimelineEvent,
  lookup: Map<string, Event>
): TimelineEvent | null {
  // server.event is a UUID string referencing an event in the events array
  const eventRef = server.event;
  const event = lookup.get(eventRef);
  if (!event) {
    return null;
  }
  return new TimelineEvent(event);
}

function convertServerSpan(
  server: ServerTimelineSpan,
  lookup: Map<string, Event>
): TimelineSpan {
  const content = server.content
    .map((item) => convertServerContentItem(item, lookup))
    .filter((item): item is TimelineEvent | TimelineSpan => item !== null);
  const branches = server.branches
    .map((b) => convertServerSpan(b, lookup))
    .filter((b) => b.content.length > 0 || b.branches.length > 0);

  return new TimelineSpan({
    id: server.id,
    name: server.name,
    spanType: server.span_type ?? null,
    branchedFrom: server.branched_from ?? null,
    content,
    branches,
    description: server.description ?? undefined,
    utility: server.utility,
    toolInvoked: server.tool_invoked,
    agentResult: server.agent_result ?? undefined,
    outline: server.outline ?? undefined,
  });
}

// =============================================================================
// Splice — reconstruct a branch's full event stream
// =============================================================================

/**
 * Reconstruct `target`'s full event stream by concatenating ancestor prefixes.
 *
 * For each ancestor, take events up to the `AnchorEvent` matching the next
 * child's `branchedFrom`, strip the `:{span_id}` suffix from span IDs, and
 * concatenate. The result is what a standalone unbranched run of `target`'s
 * lineage would have produced.
 */
export function splice(root: TimelineSpan, target: TimelineSpan): Event[] {
  const chain = ancestorChain(root, target);
  const out: Event[] = [];
  for (let i = 0; i < chain.length; i++) {
    const node = chain[i]!;
    let events = node.content
      .filter((it): it is TimelineEvent => it.type === "event")
      .map((it) => it.event);
    if (i + 1 < chain.length) {
      const anchor = chain[i + 1]!.branchedFrom;
      if (!anchor) {
        out.length = 0;
        continue;
      }
      const cut = events.findIndex(
        (e) => e.event === "anchor" && e.anchor_id === anchor
      );
      if (cut < 0) {
        throw new Error(
          `splice: anchor ${anchor} not found in ${node.name} (${node.id})`
        );
      }
      events = events.slice(0, cut + 1);
    }
    const suffix = `:${node.id}`;
    for (const e of events) out.push(stripSuffix(e, suffix, node.id));
  }
  return out;
}

/** Splice `target` and rebuild it as a standalone (unbranched) Timeline. */
export function spliceToTimeline(
  root: TimelineSpan,
  target: TimelineSpan
): Timeline {
  return buildTimeline(splice(root, target));
}

function ancestorChain(
  root: TimelineSpan,
  target: TimelineSpan
): TimelineSpan[] {
  const path: TimelineSpan[] = [];
  function walk(node: TimelineSpan): boolean {
    path.push(node);
    if (node === target || node.id === target.id) return true;
    for (const child of node.branches) if (walk(child)) return true;
    path.pop();
    return false;
  }
  if (!walk(root)) {
    throw new Error(`TimelineSpan ${target.id} not reachable from root`);
  }
  return path;
}

export function stripSuffix(e: Event, suffix: string, trajId: string): Event {
  const update: { span_id?: string; id?: string; parent_id?: string } = {};
  if (e.span_id?.endsWith(suffix)) {
    update.span_id = e.span_id.slice(0, -suffix.length);
  } else if (e.span_id === trajId) {
    update.span_id = "";
  }
  if (e.event === "span_begin" || e.event === "span_end") {
    if (e.id.endsWith(suffix)) {
      update.id = e.id.slice(0, -suffix.length);
    }
  }
  if (e.event === "span_begin" && e.parent_id != null) {
    if (e.parent_id.endsWith(suffix)) {
      update.parent_id = e.parent_id.slice(0, -suffix.length);
    } else if (e.parent_id === trajId) {
      update.parent_id = "";
    }
  }
  if (Object.keys(update).length === 0) return e;
  // The spread only overwrites fields the branches above proved `e` has, so
  // the result is the same union member with three strings rewritten.
  return { ...e, ...update };
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get tokens from an event (ModelEvent only).
 */
function getEventTokens(event: Event): number {
  if (event.event === "model") {
    const usage = event.output.usage;
    if (usage) {
      const inputTokens = usage.input_tokens;
      const cacheRead = usage.input_tokens_cache_read ?? 0;
      const cacheWrite = usage.input_tokens_cache_write ?? 0;
      const outputTokens = usage.output_tokens;
      return inputTokens + cacheRead + cacheWrite + outputTokens;
    }
  }
  return 0;
}

/**
 * Return the earliest start time among nodes.
 * Requires at least one node (all nodes have non-null startTime).
 */
function minStartTime(nodes: (TimelineEvent | TimelineSpan)[]): Date {
  const first = nodes[0];
  if (!first) {
    throw new Error("minStartTime requires at least one node");
  }
  return nodes.reduce(
    (min, n) => (n.startTime() < min ? n.startTime() : min),
    first.startTime()
  );
}

/**
 * Return the latest end time among nodes.
 * Requires at least one node (all nodes have non-null endTime).
 */
function maxEndTime(nodes: (TimelineEvent | TimelineSpan)[]): Date {
  const first = nodes[0];
  if (!first) {
    throw new Error("maxEndTime requires at least one node");
  }
  return nodes.reduce(
    (max, n) => (n.endTime() > max ? n.endTime() : max),
    first.endTime()
  );
}

/**
 * Sum total tokens across all nodes.
 */
function sumTokens(nodes: (TimelineEvent | TimelineSpan)[]): number {
  return nodes.reduce((sum, n) => sum + n.totalTokens(), 0);
}

const IDLE_THRESHOLD_MS = 300_000; // 5 minutes

/**
 * Compute idle time using gap-based detection between children.
 *
 * Any gap > 5 min between consecutive children (sorted by startTime)
 * is counted as idle. Children's own idleTime is summed recursively.
 */
export function computeIdleTime(
  content: (TimelineEvent | TimelineSpan)[],
  startTime: Date,
  endTime: Date
): number {
  if (content.length === 0) return 0;

  const sorted = [...content].sort(
    (a, b) => a.startTime().getTime() - b.startTime().getTime()
  );
  let idleMs = 0;

  // Sum children's own idle time
  for (const child of sorted) {
    idleMs += child.idleTime() * 1000;
  }

  // Gap: span start → first child
  const firstGap = sorted[0]!.startTime().getTime() - startTime.getTime();
  if (firstGap > IDLE_THRESHOLD_MS) idleMs += firstGap;

  // Gaps between consecutive children
  for (let i = 1; i < sorted.length; i++) {
    const gap =
      sorted[i]!.startTime().getTime() - sorted[i - 1]!.endTime().getTime();
    if (gap > IDLE_THRESHOLD_MS) idleMs += gap;
  }

  // Gap: last child → span end
  const lastGap =
    endTime.getTime() - sorted[sorted.length - 1]!.endTime().getTime();
  if (lastGap > IDLE_THRESHOLD_MS) idleMs += lastGap;

  return Math.max(0, idleMs / 1000);
}

// =============================================================================
// Node Creation
// =============================================================================

/**
 * Create a TimelineEvent from an Event.
 */
function createTimelineEvent(event: Event): TimelineEvent {
  return new TimelineEvent(event);
}

/**
 * Create a TimelineSpan with computed properties.
 */
function createTimelineSpan(
  id: string,
  name: string,
  spanType: string | null,
  content: (TimelineEvent | TimelineSpan)[],
  utility: boolean = false,
  branches: TimelineSpan[] = [],
  description?: string,
  branchedFrom: string | null = null
): TimelineSpan {
  if (content.length === 0) {
    throw new Error(
      `createTimelineSpan called with empty content for span "${name}" (id=${id}). ` +
        "Callers must guard against empty content before calling the factory."
    );
  }
  return new TimelineSpan({
    id,
    name: name.toLowerCase(),
    spanType,
    content,
    utility,
    branches,
    description,
    branchedFrom,
  });
}

// =============================================================================
// Span Tree Building
// =============================================================================

/**
 * Build a span tree from a flat event list.
 *
 * Parses span_begin/span_end events to create hierarchical structure.
 * Events are associated with spans via their span_id field.
 */
function buildSpanTree(events: Event[]): TreeItem[] {
  const root: TreeItem[] = [];
  const spansById = new Map<string, SpanNode>();
  const spanStack: SpanNode[] = [];

  // Pre-register all spans so events referencing later spans can find them.
  // This handles the case where span_begin events appear after the events
  // they contain (e.g. agent spans appended to the transcript after their
  // child events).
  for (const event of events) {
    if (event.event === "span_begin") {
      const span: SpanNode = {
        id: event.id,
        name: event.name,
        type: event.type ?? undefined,
        parentId: event.parent_id,
        metadata: event.metadata ?? undefined,
        children: [],
        beginEvent: event,
      };
      spansById.set(span.id, span);
    }
  }

  // Main loop: build hierarchy and assign events to spans
  for (const event of events) {
    if (event.event === "span_begin") {
      const span = spansById.get(event.id)!;

      // Determine where to place this span
      if (span.parentId && spansById.has(span.parentId)) {
        // Parent span exists - add as child
        spansById.get(span.parentId)!.children.push(span);
      } else if (spanStack.length > 0) {
        // No explicit parent but we have an open span - add to current span
        const currentSpan = spanStack[spanStack.length - 1];
        if (currentSpan) {
          currentSpan.children.push(span);
        }
      } else {
        // Top-level span
        root.push(span);
      }
      spanStack.push(span);
    } else if (event.event === "span_end") {
      // Store end event on the span and pop the stack
      const endSpan = spansById.get(event.id);
      if (endSpan) {
        endSpan.endEvent = event;
      }
      if (spanStack.length > 0) {
        spanStack.pop();
      }
    } else {
      // Regular event - add to appropriate span based on span_id
      const spanId = (event as { span_id?: string | null }).span_id;

      if (spanId && spansById.has(spanId)) {
        spansById.get(spanId)!.children.push(event);
      } else if (spanStack.length > 0) {
        // No span_id but we have an open span - add to current span
        const currentSpan = spanStack[spanStack.length - 1];
        if (currentSpan) {
          currentSpan.children.push(event);
        }
      } else {
        // No span context - add to root
        root.push(event);
      }
    }
  }

  // Sort each span's children by timestamp to ensure chronological order,
  // since events with a matching span_id may appear before the span_begin
  // in the array.
  const getTimestamp = (item: TreeItem): string => {
    if (isSpanNode(item)) {
      return item.beginEvent.timestamp;
    }
    return item.timestamp;
  };
  for (const span of spansById.values()) {
    span.children.sort((a, b) =>
      getTimestamp(a).localeCompare(getTimestamp(b))
    );
  }

  return root;
}

/**
 * Flatten a tree item recursively to get all events.
 */
function eventSequence(items: TreeItem[]): Event[] {
  const events: Event[] = [];
  for (const item of items) {
    if (isSpanNode(item)) {
      events.push(...eventSequence(item.children));
    } else {
      events.push(item);
    }
  }
  return events;
}

/**
 * Check if a span contains any ModelEvent (recursively).
 */
function containsModelEvents(span: SpanNode): boolean {
  for (const child of span.children) {
    if (isSpanNode(child)) {
      if (containsModelEvents(child)) {
        return true;
      }
    } else if (child.event === "model") {
      return true;
    }
  }
  return false;
}

/**
 * Check if a span has any descendant span with type="agent".
 *
 * Used to suppress tool→agent classification when the tool already
 * wraps an explicit agent span (which will represent the agent itself).
 */
function containsAgentSpan(span: SpanNode): boolean {
  for (const child of span.children) {
    if (isSpanNode(child)) {
      if (child.type === "agent" || containsAgentSpan(child)) {
        return true;
      }
    }
  }
  return false;
}

// =============================================================================
// Node Building from Tree
// =============================================================================

/**
 * Convert an Event to a TimelineEvent or TimelineSpan.
 *
 * Handles ToolEvents that spawn nested agents, recursively processing
 * nested events to detect further agent spawning.
 */
/**
 * ToolEvent.events is `unknown[]` in the schema. Shallow check: the timeline
 * dispatches on `event`, and anything without one can't be placed.
 */
const isEvent = (value: unknown): value is Event =>
  isRecord(value) && typeof value["event"] === "string";

function eventToNode(event: Event): TimelineEvent | TimelineSpan {
  if (event.event === "tool") {
    const agentName = event.agent;
    const nestedEvents = event.events.filter(isEvent);

    if (agentName && nestedEvents.length > 0) {
      // Recursively process nested events to handle nested tool agents
      const nestedContent: (TimelineEvent | TimelineSpan)[] = nestedEvents.map(
        (e) => eventToNode(e)
      );

      if (nestedContent.length > 0) {
        const span = createTimelineSpan(
          `tool-agent-${event.id}`,
          agentName,
          "agent",
          nestedContent
        );
        // Spawned by a ToolEvent — explicit user-intended subagent.
        span.toolInvoked = true;
        // Capture agent result from the spawning ToolEvent. (Codex sub-agents
        // are span-based — they don't reach this ToolEvent path — but apply the
        // Codex reshape defensively so any tool-spawned Codex result is handled
        // consistently rather than rendered as raw JSON.)
        const agentResult = extractToolEventResult(event.result);
        if (agentResult) {
          span.agentResult = codexResultText(
            event.function,
            event.result,
            agentResult
          );
        }
        return span;
      }
    }
  }
  return createTimelineEvent(event);
}

/**
 * Check if a SpanNode represents an agent trajectory.
 *
 * Agent spans are:
 * - Explicit agent spans (type="agent")
 * - Solver spans that wrap a genuine agent (e.g. an @agent run via as_solver)
 * - Tool spans containing model events (tool-spawned agents)
 *
 * Primitive solver spans (system_message, generate, use_tools, ...) wrap no
 * agent of their own, so they are not agent trajectories — they get unrolled
 * into their parent rather than occupying a swimlane.
 */
function isAgentSpan(span: SpanNode): boolean {
  if (span.type === "agent") {
    return true;
  }
  if (span.type === "solver") {
    return containsAgentSpan(span);
  }
  if (
    span.type === "tool" &&
    containsModelEvents(span) &&
    !containsAgentSpan(span)
  ) {
    return true;
  }
  return false;
}

/**
 * Convert a tree item (SpanNode or Event) to a TimelineEvent, TimelineSpan,
 * or null if the resulting span would be empty.
 */
function treeItemToNode(item: TreeItem): TimelineEvent | TimelineSpan | null {
  if (isSpanNode(item)) {
    if (item.type === "agent" || item.type === "solver") {
      return buildSpanFromAgentSpan(item);
    } else {
      // Non-agent span - may be tool span with model events
      return buildSpanFromGenericSpan(item);
    }
  } else {
    return eventToNode(item);
  }
}

/**
 * Build a TimelineSpan from a SpanNode with type='agent'.
 */
function buildSpanFromAgentSpan(
  span: SpanNode,
  extraItems?: TreeItem[]
): TimelineSpan | null {
  const content: (TimelineEvent | TimelineSpan)[] = [];

  // Add any extra items first (orphan events)
  if (extraItems) {
    for (const item of extraItems) {
      if (isSpanNode(item) && !isAgentSpan(item)) {
        unrollSpan(item, content);
      } else {
        const node = treeItemToNode(item);
        if (node !== null) {
          content.push(node);
        }
      }
    }
  }

  // Process span children with branch awareness
  const [childContent, branches] = processChildren(span.children);
  content.push(...childContent);

  if (content.length === 0) {
    return null;
  }

  const description =
    typeof span.metadata?.description === "string"
      ? span.metadata.description
      : undefined;

  return createTimelineSpan(
    span.id,
    span.name,
    "agent",
    content,
    false,
    branches,
    description
  );
}

/**
 * Build a TimelineSpan from a non-agent SpanNode.
 *
 * If the span is a tool span (type="tool") containing model events,
 * we treat it as a tool-spawned agent (spanType="agent").
 */
function buildSpanFromGenericSpan(span: SpanNode): TimelineSpan | null {
  const [content, branches] = processChildren(span.children);

  if (content.length === 0) {
    return null;
  }

  // Determine the spanType based on span type and content. A tool span that
  // recursively contains model events is treated as a tool-spawned agent
  // (e.g. bridge-style tools like Claude Code that emit raw model events).
  // But if the tool already wraps an explicit agent span, leave the
  // classification alone — the inner agent span represents the agent.
  const spanType: string | null =
    span.type === "tool" &&
    containsModelEvents(span) &&
    !containsAgentSpan(span)
      ? "agent"
      : (span.type ?? null);

  return createTimelineSpan(
    span.id,
    span.name,
    spanType,
    content,
    false,
    branches
  );
}

/**
 * Recursively unwrap solver-type spans that contain exactly one agent child.
 *
 * This flattens unnecessary nesting (e.g. a "react" solver wrapping a single
 * agent) so the timeline shows the agent directly.
 */
function unwrapSolverSpan(span: SpanNode): SpanNode {
  while (span.type === "solver") {
    const agentChildren = span.children.filter(
      (child): child is SpanNode => isSpanNode(child) && child.type === "agent"
    );
    if (agentChildren.length !== 1) {
      break;
    }
    // Only collapse the redundant solver→agent nesting when the solver carries
    // no displayable events of its own. If it logged any other meaningful
    // events, keep the solver span so those survive in the lane (when
    // as_solver() runs an agent it emits state/store events, if these are the
    // only other kind of events it's fine to drop them)
    if (
      span.children.some(
        (c) => !isSpanNode(c) && c.event !== "state" && c.event !== "store"
      )
    ) {
      break;
    }
    span = agentChildren[0]!;
  }
  return span;
}

/**
 * Build agent hierarchy from the solvers span.
 *
 * Looks for explicit agent spans (type='agent') within the solvers span.
 * If found, builds the agent tree from those spans. If not found, uses
 * the solvers span itself as the agent container.
 */
function buildAgentFromSolversSpan(solversSpan: SpanNode): TimelineSpan | null {
  if (solversSpan.children.length === 0) {
    return null;
  }

  // Look for agent spans within solvers
  const agentSpans: SpanNode[] = [];
  const otherItems: TreeItem[] = [];

  for (const child of solversSpan.children) {
    if (isSpanNode(child) && isAgentSpan(child)) {
      agentSpans.push(child);
    } else {
      otherItems.push(child);
    }
  }

  if (agentSpans.length > 0) {
    // Build from explicit agent spans
    const firstAgentSpan = agentSpans[0];
    if (agentSpans.length === 1 && firstAgentSpan) {
      const target = unwrapSolverSpan(firstAgentSpan);
      const result = buildSpanFromAgentSpan(target, otherItems);
      if (result !== null) {
        return result;
      }
      // Agent span had no content — return an empty span preserving identity
      return new TimelineSpan({
        id: target.id,
        name: target.name.toLowerCase(),
        spanType: "agent",
      });
    } else {
      // Multiple agent spans - create root containing all
      const children: (TimelineEvent | TimelineSpan)[] = [];
      for (const span of agentSpans) {
        const node = buildSpanFromAgentSpan(span);
        if (node !== null) {
          children.push(node);
        }
      }
      // Add any orphan events at the start
      for (const item of otherItems) {
        if (isSpanNode(item) && !isAgentSpan(item)) {
          const orphanContent: (TimelineEvent | TimelineSpan)[] = [];
          unrollSpan(item, orphanContent);
          for (let i = orphanContent.length - 1; i >= 0; i--) {
            children.unshift(orphanContent[i]!);
          }
        } else {
          const node = treeItemToNode(item);
          if (node !== null) {
            children.unshift(node);
          }
        }
      }
      if (children.length === 0) {
        return null;
      }
      return createTimelineSpan("root", "main", "agent", children);
    }
  } else {
    // No explicit agent spans - use solvers span itself as the agent container
    const [content, branches] = processChildren(solversSpan.children);
    if (content.length === 0) {
      return null;
    }
    return createTimelineSpan(
      solversSpan.id,
      solversSpan.name,
      "agent",
      content,
      false,
      branches
    );
  }
}

/**
 * Build agent from a list of tree items when no explicit phase spans exist.
 *
 * Creates a synthetic "main" agent containing all tree items as content.
 */
function buildAgentFromTree(tree: TreeItem[]): TimelineSpan | null {
  const [content, branches] = processChildren(tree);

  if (content.length === 0) {
    return null;
  }

  return createTimelineSpan("main", "main", "agent", content, false, branches);
}

/**
 * Dissolve a non-agent span, emitting its begin/end as regular events.
 *
 * Recursively unrolls nested non-agent spans while preserving any
 * nested agent spans as TimelineSpan nodes.
 */
function unrollSpan(
  span: SpanNode,
  into: (TimelineEvent | TimelineSpan)[]
): void {
  // Emit span begin event
  into.push(createTimelineEvent(span.beginEvent));

  // An agent span discovered as a direct child of a tool span being
  // unrolled was invoked as a tool (the agent tool / task / as_tool /
  // handoff). Mark it so classifyUtilityAgents leaves it alone — tool-invoked
  // subagents are explicit user intent, not internal helper calls.
  const parentIsTool = span.type === "tool";

  // Process children: recurse into non-agent spans, keep agent spans
  for (const child of span.children) {
    if (isSpanNode(child)) {
      if (isAgentSpan(child)) {
        const node = treeItemToNode(child);
        if (node === null) continue;
        if (node.type === "span" && node.content.length === 0) continue;
        if (parentIsTool && node.type === "span") {
          node.toolInvoked = true;
        }
        into.push(node);
      } else {
        unrollSpan(child, into);
      }
    } else {
      into.push(eventToNode(child));
    }
  }

  // Emit span end event
  if (span.endEvent) {
    into.push(createTimelineEvent(span.endEvent));
  }
}

// =============================================================================
// Branch Processing
// =============================================================================

/**
 * Process a span's children with branch awareness.
 *
 * Collects adjacent type="branch" SpanNode runs and builds branch TimelineSpan
 * objects from those that contain a BranchEvent. Branch spans without a
 * BranchEvent are processed as normal content.
 */
function processChildren(
  children: TreeItem[]
): [(TimelineEvent | TimelineSpan)[], TimelineSpan[]] {
  const content: (TimelineEvent | TimelineSpan)[] = [];
  const branches: TimelineSpan[] = [];
  let branchRun: SpanNode[] = [];

  function flushBranchRun(
    run: SpanNode[],
    parentContent: (TimelineEvent | TimelineSpan)[]
  ): TimelineSpan[] {
    const result: TimelineSpan[] = [];
    for (const span of run) {
      const branchEvent = findBranchEvent(span);
      if (branchEvent === null) {
        // No BranchEvent — process as normal content
        processSpanAsContent(span, parentContent);
        continue;
      }
      const branchContent: (TimelineEvent | TimelineSpan)[] = [];
      for (const child of span.children) {
        if (isSpanNode(child) && !isAgentSpan(child)) {
          unrollSpan(child, branchContent);
        } else {
          const node = treeItemToNode(child);
          if (node === null) continue;
          branchContent.push(node);
        }
      }
      if (branchContent.length === 0) continue;
      result.push(
        createTimelineSpan(
          span.id,
          span.name || "branch",
          "branch",
          branchContent,
          false,
          [],
          undefined,
          branchEvent.from_anchor
        )
      );
    }
    return result;
  }

  for (const item of children) {
    if (isSpanNode(item) && item.type === "branch") {
      branchRun.push(item);
    } else {
      if (branchRun.length > 0) {
        branches.push(...flushBranchRun(branchRun, content));
        branchRun = [];
      }
      if (isSpanNode(item) && !isAgentSpan(item)) {
        // Unroll: dissolve non-agent span wrapper into parent,
        // emitting begin/end as events and preserving nested agents
        unrollSpan(item, content);
      } else {
        const node = treeItemToNode(item);
        if (node === null) continue;
        content.push(node);
      }
    }
  }

  if (branchRun.length > 0) {
    branches.push(...flushBranchRun(branchRun, content));
  }

  return [content, branches];
}

/**
 * Find a BranchEvent in a span's direct children.
 */
function findBranchEvent(span: SpanNode): { from_anchor: string } | null {
  for (const child of span.children) {
    if (!isSpanNode(child) && child.event === "branch") {
      return child;
    }
  }
  return null;
}

/**
 * Process a branch span as normal content when it has no BranchEvent.
 */
function processSpanAsContent(
  span: SpanNode,
  into: (TimelineEvent | TimelineSpan)[]
): void {
  for (const child of span.children) {
    if (isSpanNode(child) && !isAgentSpan(child)) {
      unrollSpan(child, into);
    } else {
      const node = treeItemToNode(child);
      if (node === null) continue;
      if (node.type === "span" && node.content.length === 0) continue;
      into.push(node);
    }
  }
}

// =============================================================================
// Branch Relocation
// =============================================================================

// =============================================================================
// Utility Event Wrapping (bridge-based agents)
// =============================================================================

/**
 * Strip the per-call billing header from a system prompt.
 *
 * Claude Code prepends a line like `x-anthropic-billing-header: ...`
 * with a varying `cch=` hash. Removing it lets us compare prompts
 * across calls.
 */
function normalizeSystemPrompt(prompt: string): string {
  if (prompt.startsWith("x-anthropic-billing-header:")) {
    const idx = prompt.indexOf("\n");
    if (idx !== -1) {
      return prompt.substring(idx + 1);
    }
    return ""; // prompt was only the header line
  }
  return prompt;
}

/**
 * Extract and normalize the system prompt from a single ModelEvent.
 */
function getSystemPromptForEvent(event: ModelEvent): string | null {
  const input = event.input;
  for (const msg of input) {
    if (msg.role === "system") {
      let raw: string;
      if (typeof msg.content === "string") {
        raw = msg.content;
      } else if (Array.isArray(msg.content)) {
        const parts: string[] = [];
        for (const c of msg.content) {
          if ("text" in c && typeof c.text === "string") {
            parts.push(c.text);
          }
        }
        raw = parts.join("\n");
      } else {
        return null;
      }
      // Null rather than "" for prompts that are empty after normalization
      // (e.g. only the billing header) — keeps empty prompts out of
      // primary-trajectory comparisons and prompt inheritance.
      const normalized = normalizeSystemPrompt(raw);
      return normalized ? normalized : null;
    }
  }
  return null;
}

/**
 * Check whether a ModelEvent's output contains tool calls.
 */
function hasToolCalls(event: ModelEvent): boolean {
  const choices = event.output.choices;
  if (choices.length > 0) {
    const msg = choices[0]!.message;
    if (msg.tool_calls && msg.tool_calls.length > 0) {
      return true;
    }
  }
  return false;
}

/**
 * Wrap foreign-prompt model calls as synthetic utility spans.
 *
 * Within bridge-based agent spans (e.g. Claude Code), short extraction
 * model calls use a different system prompt and produce no tool calls.
 * This function detects them and wraps each one in a TimelineSpan
 * with utility=true so downstream code treats them as utility agents.
 *
 * The primary trajectory is identified by the system prompt of the first
 * tool-calling ModelEvent. When a span contains no tool-calling model
 * events there is no agentic loop to distinguish helpers from — plain
 * workflows of generate() calls routinely mix system prompts — so
 * foreign-prompt wrapping is skipped entirely (warmup calls are still
 * wrapped; they are identified independently of prompts).
 *
 * A call whose recorded `origin` is not "agent" is utility and never sets
 * the primary prompt. A call recorded as "agent" goes through the rules
 * above, as a call with no `origin` does, since a bridged agent's warm-ups
 * and helper calls are recorded as "agent".
 *
 * Operates recursively on the entire span tree.
 */
function wrapUtilityEvents(agent: TimelineSpan): void {
  // The primary prompt comes only from a tool-calling ModelEvent: without
  // an agentic loop there is no primary trajectory (see docstring).
  let primaryPrompt: string | null = null;
  for (const item of agent.content) {
    if (item.type === "event" && item.event.event === "model") {
      const modelEvt = item.event;
      if (hasToolCalls(modelEvt) && mayBeTurn(modelEvt)) {
        primaryPrompt = getSystemPromptForEvent(modelEvt);
        break;
      }
    }
  }

  // Fall back to a position-derived id for uuid-less (legacy) events so
  // ids stay unique and deterministic across rebuilds.
  const utilityWrapper = (item: TimelineEvent, index: number) => {
    const wrapper = createTimelineSpan(
      `utility-${item.event.uuid ?? `${agent.id}-${index}`}`,
      "utility",
      "agent",
      [item]
    );
    wrapper.utility = true;
    return wrapper;
  };

  // --- Scan and wrap utility candidates ---
  const originalSpans = agent.content.filter(
    (item): item is TimelineSpan => item.type === "span"
  );
  const newContent: (TimelineEvent | TimelineSpan)[] = [];
  for (const [index, item] of agent.content.entries()) {
    if (item.type === "event" && item.event.event === "model") {
      const modelEvt = item.event;

      if (!mayBeTurn(modelEvt)) {
        newContent.push(utilityWrapper(item, index));
        continue;
      }

      // Warmup/cache-priming call (max_tokens=1)
      if (isWarmupCall(modelEvt)) {
        newContent.push(utilityWrapper(item, index));
        continue;
      }

      if (primaryPrompt !== null && !hasToolCalls(modelEvt)) {
        const evtPrompt = getSystemPromptForEvent(modelEvt);
        if (evtPrompt !== null && evtPrompt !== primaryPrompt) {
          newContent.push(utilityWrapper(item, index));
          continue;
        }
      }
    }
    newContent.push(item);
  }

  agent.content = newContent;

  // Recurse into the span's original children only — not the synthetic
  // wrappers created above: a wrapper holds a single already-processed
  // event, and re-entering it would wrap a warmup call again, forever.
  // wrapUtilityEvents(branch) recurses into the branch's own children.
  for (const item of originalSpans) {
    wrapUtilityEvents(item);
  }
  for (const branch of agent.branches) {
    wrapUtilityEvents(branch);
  }
}

/**
 * Whether a model call with this origin is one of its conversation's agent
 * turns; a kind added later reads as not a turn.
 */
function isAgentTurn(origin: string | null | undefined): boolean {
  return origin === "agent";
}

/**
 * An agent turn by its recorded origin, or a call that records none.
 */
function mayBeTurn(event: ModelEvent): boolean {
  return event.origin == null || isAgentTurn(event.origin);
}

function isWarmupCall(event: ModelEvent): boolean {
  if (event.config.max_tokens == null || event.config.max_tokens > 1) {
    return false;
  }
  // Check that the last user message is a single word
  const input = event.input;
  for (let i = input.length - 1; i >= 0; i--) {
    const msg = input[i];
    if (msg?.role === "user") {
      if (typeof msg.content === "string") {
        return msg.content.trim().split(/\s+/).length <= 1;
      }
      return false;
    }
  }
  return false;
}

// =============================================================================
// Utility Agent Classification
// =============================================================================

/**
 * Extract the normalized system prompt from the first ModelEvent in span's
 * direct content (see getSystemPromptForEvent).
 */
function getSystemPrompt(span: TimelineSpan): string | null {
  for (const item of span.content) {
    if (item.type === "event" && item.event.event === "model") {
      return getSystemPromptForEvent(item.event);
    }
  }
  return null; // No ModelEvent found
}

/**
 * Check if span has a single turn or single tool-calling turn.
 *
 * A single turn is 1 ModelEvent with no ToolEvents.
 * A single tool-calling turn is 2 ModelEvents with a ToolEvent between them.
 */
function isSingleTurn(span: TimelineSpan): boolean {
  // Collect direct events (not child spans) with their types
  const directEvents: string[] = [];
  for (const item of span.content) {
    if (item.type === "event") {
      if (item.event.event === "model") {
        directEvents.push("model");
      } else if (item.event.event === "tool") {
        directEvents.push("tool");
      }
    }
  }

  const modelCount = directEvents.filter((e) => e === "model").length;
  const toolCount = directEvents.filter((e) => e === "tool").length;

  // Single turn: exactly 1 model event
  if (modelCount === 1) {
    return true;
  }

  // Single tool-calling turn: 2 model events with tool event(s) between
  if (modelCount === 2 && toolCount >= 1) {
    const firstModel = directEvents.indexOf("model");
    const secondModel = directEvents.lastIndexOf("model");
    const between = directEvents.slice(firstModel + 1, secondModel);
    return between.includes("tool");
  }

  return false;
}

/**
 * Check whether span's direct content contains a tool-calling ModelEvent.
 */
function hasAgenticLoop(span: TimelineSpan): boolean {
  for (const item of span.content) {
    if (
      item.type === "event" &&
      item.event.event === "model" &&
      hasToolCalls(item.event)
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Classify utility agents in the tree via post-processing.
 *
 * An agent is utility if it has a single turn (or single tool-calling turn)
 * and a different system prompt than its parent. Classification only applies
 * when the parent runs an agentic (tool-calling) loop: absent a loop there
 * is no main trajectory for a helper to be subordinate to — plain workflows
 * of generate() calls routinely mix system prompts.
 */
function classifyUtilityAgents(
  node: TimelineSpan,
  parentSystemPrompt: string | null = null,
  parentHasLoop: boolean = false
): void {
  const agentSystemPrompt = getSystemPrompt(node);

  // Classify this node (root agent is never utility). Tool-invoked subagents
  // (the agent tool / task / as_tool / handoff) are explicit user-intended
  // sub-trajectories — never utility — even when single-turn (e.g. a cancelled
  // background subagent). Foreign-prompt helper calls are handled separately
  // by wrapUtilityEvents.
  if (
    parentSystemPrompt !== null &&
    parentHasLoop &&
    agentSystemPrompt !== null &&
    !node.toolInvoked
  ) {
    if (agentSystemPrompt !== parentSystemPrompt && isSingleTurn(node)) {
      node.utility = true;
    }
  }

  // Recurse into child spans. Pure grouping spans (no direct model events)
  // inherit from the parent, mirroring the prompt inheritance.
  const effectivePrompt = agentSystemPrompt ?? parentSystemPrompt;
  const hasModelEvents = node.content.some(
    (item) => item.type === "event" && item.event.event === "model"
  );
  const effectiveHasLoop = hasModelEvents
    ? hasAgenticLoop(node)
    : parentHasLoop;
  for (const item of node.content) {
    if (item.type === "span") {
      classifyUtilityAgents(item, effectivePrompt, effectiveHasLoop);
    }
  }
}

// =============================================================================
// Main Builder
// =============================================================================

/**
 * Build a Timeline from a flat event list.
 *
 * Transforms a flat event stream into a hierarchical Timeline tree
 * with agent-centric interpretation. The pipeline has two phases:
 *
 * **Phase 1 — Structure extraction:**
 *
 * Parses span_begin/span_end events into a tree, then looks for
 * top-level phase spans ("init", "solvers", "scorers"):
 * - If present, partitions events into init (setup), agent (solvers),
 *   and scoring sections.
 * - If absent, treats the entire event stream as the agent.
 *
 * **Phase 2 — Agent classification:**
 *
 * Within the agent section, spans are classified as agents or unrolled:
 * - type="agent"                → TimelineSpan (spanType="agent")
 * - type="solver" wrapping agent → TimelineSpan (spanType="agent")
 * - type="solver" (primitive)   → Unrolled into parent
 * - type="tool" + ModelEvents   → TimelineSpan (spanType="agent")
 * - ToolEvent with agent field  → TimelineSpan (spanType="agent")
 * - type="tool" (no models)     → Unrolled into parent
 * - Any other span type         → Unrolled into parent
 *
 * "Unrolled" means the span wrapper is removed and its child events
 * dissolve into the parent's content list.
 *
 * **Phase 3 — Post-processing passes:**
 * - Utility event wrapping (bridge-based agents with foreign prompts)
 * - Utility agent classification (single-turn, different system prompt)
 * - Recursive branch classification
 */
// =============================================================================
// Span Result Extraction
// =============================================================================

/**
 * Returns the pre-extracted agent result for a span.
 * Results are extracted during timeline building by `extractAgentResults()`.
 */
export function getSpanToolResult(span: TimelineSpan): string | undefined {
  return span.agentResult;
}

/**
 * Reshape a Codex sub-agent tool result into nicer markdown when applicable
 * (e.g. `spawn_agent`'s `{agent_id, nickname}` → a compact name line); falls
 * back to the raw extracted text for non-Codex tools.
 */
function codexResultText(
  fn: string | undefined,
  content: unknown,
  fallback: string
): string {
  if (fn) {
    const markdown = codexToolMarkdown(fn, content);
    if (markdown !== undefined) {
      return markdown;
    }
  }
  return fallback;
}

/**
 * Extract a string result from a ToolEvent result field.
 */
function extractToolEventResult(result: unknown): string | undefined {
  if (typeof result === "string" && result) return result;
  if (Array.isArray(result)) {
    const parts: string[] = [];
    for (const c of result) {
      if (isRecord(c) && typeof c["text"] === "string") {
        parts.push(c["text"]);
      }
    }
    return parts.length > 0 ? parts.join("\n") : undefined;
  }
  return undefined;
}

/**
 * Extract agentResult for each agent sub-span.
 *
 * Three sources (checked in order):
 * 1. Tool-spawned agents: result already set during eventToNode
 * 2. Span-based agents (static flow): sibling ToolEvent with agent_span_id === span.id
 * 3. Bridge flow: next ModelEvent's input has tool message with function === span.name
 */
function extractAgentResults(parent: TimelineSpan): void {
  const content = parent.content;
  for (let i = 0; i < content.length; i++) {
    const item = content[i]!;
    if (item.type !== "span") continue;
    if (item.spanType !== "agent") {
      extractAgentResults(item);
      continue;
    }

    // Skip if already set (e.g. from tool-spawned agent construction)
    if (item.agentResult) {
      extractAgentResults(item);
      continue;
    }

    // Flow 1: sibling ToolEvent with agent_span_id matching this span
    for (const sibling of content) {
      if (
        sibling.type === "event" &&
        sibling.event.event === "tool" &&
        sibling.event.agent_span_id === item.id
      ) {
        const resultText = extractToolEventResult(sibling.event.result);
        if (resultText) {
          item.agentResult = codexResultText(
            sibling.event.function,
            sibling.event.result,
            resultText
          );
        }
        break;
      }
    }

    // Flow 2: next model event's input has tool message with matching tool_call_id
    // The span ID follows the pattern "agent-{tool_call_id}" in bridge flow.
    const toolCallId = item.id.startsWith("agent-") ? item.id.slice(6) : null;

    if (!item.agentResult && toolCallId) {
      for (let j = i + 1; j < content.length; j++) {
        const nextItem = content[j]!;
        if (nextItem.type !== "event") continue;
        if (nextItem.event.event === "model") {
          const modelEvent = nextItem.event;
          for (const msg of modelEvent.input) {
            if (msg.role === "tool" && msg.tool_call_id === toolCallId) {
              const text = extractToolEventResult(msg.content);
              if (text) {
                item.agentResult = codexResultText(
                  msg.function ?? undefined,
                  msg.content,
                  text
                );
              }
            }
          }
          if (item.agentResult) break;
        }
      }
    }

    // Recurse into child spans
    extractAgentResults(item);
  }
}

/**
 * Derive a display label for a utility agent span.
 *
 * Synthetic utility spans created by `wrapUtilityEvents` are named "utility"
 * which is redundant when paired with a "utility:" prefix. For these, extract
 * the model name from the inner model event. For classified utility agents
 * (real spans with meaningful names like "explore"), keep the existing name.
 */
export function getUtilityAgentLabel(span: TimelineSpan): string {
  const name = span.name.toLowerCase();
  if (name !== "utility") return name;

  for (const item of span.content) {
    if (item.type === "event" && item.event.event === "model") {
      return item.event.model;
    }
  }
  return name;
}

export function buildTimeline(events: Event[]): Timeline {
  if (events.length === 0) {
    const emptyRoot = new TimelineSpan({
      id: "root",
      name: "main",
      spanType: null,
    });
    return { name: "Default", description: "", root: emptyRoot };
  }

  // Build span tree from events
  const tree = buildSpanTree(events);

  // Find top-level spans by name
  const topSpans = new Map<string, SpanNode>();
  for (const item of tree) {
    if (
      isSpanNode(item) &&
      (item.name === "init" ||
        item.name === "solvers" ||
        item.name === "scorers")
    ) {
      topSpans.set(item.name, item);
    }
  }

  // Check for explicit phase spans (init, solvers, or scorers)
  const hasPhaseSpans =
    topSpans.has("init") || topSpans.has("solvers") || topSpans.has("scorers");

  // Top-level items that don't belong to a phase span — e.g. a SampleLimitEvent
  // (span_id=null) recorded when an eval is interrupted. The phase-span branch
  // below partitions events strictly by init/solvers/scorers, so these would
  // otherwise be dropped from the tree and stay invisible in swimlane views
  // (ts-mono#178). The no-phase-spans branch already keeps them via
  // buildAgentFromTree(tree).
  const orphanNodes = hasPhaseSpans
    ? tree
        .filter(
          (item) => !(isSpanNode(item) && topSpans.get(item.name) === item)
        )
        .map(treeItemToNode)
        .filter((n): n is TimelineEvent | TimelineSpan => n !== null)
    : [];

  let root: TimelineSpan;

  if (hasPhaseSpans) {
    // Use spans to partition events
    const initSpan = topSpans.get("init");
    const solversSpan = topSpans.get("solvers");
    const scorersSpan = topSpans.get("scorers");

    // Build init span
    let initSpanObj: TimelineSpan | null = null;
    if (initSpan) {
      const initContent = eventSequence(initSpan.children).map((e) =>
        createTimelineEvent(e)
      );
      if (initContent.length > 0) {
        initSpanObj = createTimelineSpan(
          initSpan.id,
          "init",
          "init",
          initContent
        );
      }
    }

    // Build agent node from solvers
    const agentNode = solversSpan
      ? buildAgentFromSolversSpan(solversSpan)
      : null;

    // Build scoring span
    let scoringSpan: TimelineSpan | null = null;
    if (scorersSpan) {
      const scoringContent = eventSequence(scorersSpan.children).map((e) =>
        createTimelineEvent(e)
      );
      if (scoringContent.length > 0) {
        scoringSpan = createTimelineSpan(
          scorersSpan.id,
          "scoring",
          "scorers",
          scoringContent
        );
      }
    }

    if (agentNode) {
      agentNode.name = "main";
      wrapUtilityEvents(agentNode);
      classifyUtilityAgents(agentNode);
      extractAgentResults(agentNode);

      // Prepend init span to agent content
      if (initSpanObj) {
        agentNode.content = [initSpanObj, ...agentNode.content];
      }

      // Append scoring as a child span
      if (scoringSpan) {
        agentNode.content.push(scoringSpan);
      }

      root = agentNode;
    } else {
      // No solvers span — build root from init + scoring
      const rootContent: (TimelineEvent | TimelineSpan)[] = [];
      if (initSpanObj) {
        rootContent.push(initSpanObj);
      }
      if (scoringSpan) {
        rootContent.push(scoringSpan);
      }
      if (rootContent.length > 0) {
        root = createTimelineSpan("root", "main", null, rootContent);
      } else {
        root = new TimelineSpan({
          id: "root",
          name: "main",
          spanType: null,
        });
      }
    }
  } else {
    // No phase spans - treat entire tree as agent
    const agentRoot = buildAgentFromTree(tree);
    if (agentRoot) {
      wrapUtilityEvents(agentRoot);
      classifyUtilityAgents(agentRoot);
      extractAgentResults(agentRoot);
      root = agentRoot;
    } else {
      // All content was empty — construct an empty root
      root = new TimelineSpan({
        id: "root",
        name: "main",
        spanType: null,
      });
    }
  }

  // Slot orphan top-level events into the root chronologically. Root content is
  // already in timestamp order and JS sort is stable, so existing items keep
  // their relative order and orphans land at the right point.
  if (orphanNodes.length > 0) {
    root.content = [...root.content, ...orphanNodes].sort(
      (a, b) => a.startTime().getTime() - b.startTime().getTime()
    );
  }

  return { name: "Default", description: "", root };
}
